import { useState, useRef, useCallback } from 'react';
import { uid, timeAgo, formatDate, hasTextContent, stripHtml, getTimelinePreviewImage, formatCompactCount } from '../utils';
import { DEFAULT_COLORS, TIMELINE_CATEGORIES } from '../constants';
import { Storage } from '../storage';
import Icon from './Icon';
import Modal from './Modal';
import ReportModal from './ReportModal';
import AddToCollectionModal from './AddToCollectionModal';

// ─── FEED POST COMPONENT ──────────────────────────────────────────────────────

const FeedPost = ({
  tl, author, social, currentUser, onSocialUpdate, onViewTimeline, onViewProfile,
  isFollowing, onFollowToggle,
  blockedIds = new Set(), onBlock,
  onAuthRequired,
  // Trending tab props
  isTrendingTab = false, trendingRank = null,
  likes7d = 0, comments7d = 0, views7d = 0,
  // Search props
  matchedEvents = [], searchQuery = '',
}) => {

  const likes      = social.likes    || [];
  const comments   = social.comments || [];
  const authorNote = social.authorNote || '';

  const userReaction = likes.find(l => l.userId === currentUser?.id) || null;
  const isReacted    = !!userReaction;
  const isAuthor     = currentUser?.id === author.id;
  const totalComments = comments.length + comments.reduce((s, c) => s + (c.replies?.length || 0), 0);

  const colors = tl.colors || DEFAULT_COLORS;
  const sorted = [...(tl.events || [])].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
  const minDate    = sorted.length ? sorted[0].startDate : null;
  const maxDate    = sorted.length ? sorted[sorted.length - 1].endDate : null;
  const minShowEra = sorted.length ? sorted[0].startEra !== 'NONE' : false;
  const maxShowEra = sorted.length ? sorted[sorted.length - 1].endEra !== 'NONE' : false;

  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const reactionPickerTimer                         = useRef(null);
  const [showComments,       setShowComments]       = useState(false);
  const [commentText,        setCommentText]        = useState('');
  const [replyingTo,         setReplyingTo]         = useState(null);
  const [replyText,          setReplyText]          = useState('');
  const [showAuthorTip,      setShowAuthorTip]      = useState(false);
  const [editingNote,        setEditingNote]        = useState(false);
  const [noteText,           setNoteText]           = useState('');
  const [shareCopied,        setShareCopied]        = useState(false);
  const [showReport,         setShowReport]         = useState(false);
  const [hasReported,        setHasReported]        = useState(false);
  const [commentReportTarget, setCommentReportTarget] = useState(null);
  const [reportedCommentIds, setReportedCommentIds] = useState(new Set());
  const [blockConfirm,       setBlockConfirm]       = useState(null);
  const [localBlockedIds,    setLocalBlockedIds]    = useState(new Set());
  const [toast,              setToast]              = useState(null);

  // ── Bookmark state ────────────────────────────────────────────────────────────
  const [isBookmarked,     setIsBookmarked]     = useState(!!tl.userHasBookmarked);
  const [bookmarkCount,    setBookmarkCount]    = useState(tl.bookmarkCount ?? 0);
  const [bookmarkLoading,  setBookmarkLoading]  = useState(false);
  // Note prompt: null | 'showing' | 'dismissed'
  const [notePrompt,       setNotePrompt]       = useState(null);
  const [bookmarkNote,     setBookmarkNote]     = useState('');
  const notePromptTimer                         = useRef(null);

  const [showCollectionModal, setShowCollectionModal] = useState(false);

  // Comment like state
  const [commentLikes, setCommentLikes] = useState(() => {
    const cl = {};
    for (const c of comments) {
      cl[c.id] = { count: c.likeCount || 0, liked: !!c.userHasLiked };
      for (const r of (c.replies || [])) {
        cl[r.id] = { count: r.likeCount || 0, liked: !!r.userHasLiked };
      }
    }
    return cl;
  });
  const [likerTips, setLikerTips] = useState({});

  // @mention autocomplete
  const [mentionQuery,   setMentionQuery]   = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionActive,  setMentionActive]  = useState(null);
  const [mentionIndex,   setMentionIndex]   = useState(0);
  const commentTextareaRef = useRef(null);
  const replyTextareaRef   = useRef(null);
  const mentionDebounce    = useRef(null);

  const allBlockedIds = new Set([...blockedIds, ...localBlockedIds]);

  const showToast = (msg, type = 'info') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Avatar renderer ──────────────────────────────────────────────────────────
  const renderAvatar = (u, size = 32) => {
    if (!u) return <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: 'var(--surface2)' }} />;
    if (u.avatar) return <img src={u.avatar} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
    const initials = (u.name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: 'linear-gradient(135deg, var(--accent), var(--accent3))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.floor(size * 0.38), fontWeight: 700, color: 'var(--on-accent)', userSelect: 'none',
      }}>
        {initials}
      </div>
    );
  };

  const renderMentionText = (text) => {
    if (!text) return null;
    return text.split(/(@\S+)/g).map((part, i) =>
      part.startsWith('@') ? <span key={i} style={{ color: 'var(--accent)', fontWeight: 600 }}>{part}</span> : part
    );
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const REACTIONS = [
    { type: 'heart',      emoji: '❤️', label: 'Heart' },
    { type: 'fire',       emoji: '🔥', label: 'Fire' },
    { type: 'mind_blown', emoji: '🤯', label: 'Mind Blown' },
    { type: 'sad',        emoji: '😢', label: 'Sad' },
  ];

  const handleReact = (type) => {
    if (!currentUser) return;
    const existing = likes.find(l => l.userId === currentUser.id);
    let newLikes;
    if (!existing) {
      newLikes = [...likes, { userId: currentUser.id, userName: currentUser.name, userAvatar: currentUser.avatar || null, reactionType: type }];
    } else if ((existing.reactionType || 'heart') === type) {
      newLikes = likes.filter(l => l.userId !== currentUser.id);
    } else {
      newLikes = likes.map(l => l.userId === currentUser.id ? { ...l, reactionType: type } : l);
    }
    onSocialUpdate(tl.id, { ...social, likes: newLikes });
    setShowReactionPicker(false);
  };

  const handleComment = () => {
    if (!commentText.trim()) return;
    const c = {
      id: uid(), userId: currentUser.id, userName: currentUser.name,
      userAvatar: currentUser.avatar || null,
      text: commentText.trim(), timestamp: Date.now(), replies: [],
    };
    onSocialUpdate(tl.id, { ...social, comments: [...comments, c] });
    setCommentText('');
  };

  const handleReply = (commentId) => {
    if (!replyText.trim()) return;
    const r = {
      id: uid(), userId: currentUser.id, userName: currentUser.name,
      userAvatar: currentUser.avatar || null,
      text: replyText.trim(), timestamp: Date.now(),
    };
    const updated = comments.map(c =>
      c.id === commentId ? { ...c, replies: [...(c.replies || []), r] } : c
    );
    onSocialUpdate(tl.id, { ...social, comments: updated });
    setReplyText('');
    setReplyingTo(null);
  };

  const handleDeleteComment = (commentId) =>
    onSocialUpdate(tl.id, { ...social, comments: comments.filter(c => c.id !== commentId) });

  const handleDeleteReply = (commentId, replyId) =>
    onSocialUpdate(tl.id, { ...social, comments: comments.map(c =>
      c.id === commentId ? { ...c, replies: (c.replies || []).filter(r => r.id !== replyId) } : c
    )});

  const handleSaveNote = () => {
    onSocialUpdate(tl.id, { ...social, authorNote: noteText.trim() });
    setEditingNote(false);
  };

  const handleShare = () => {
    const url = `${window.location.origin}/timeline/${tl.id}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  // ── Bookmark ────────────────────────────────────────────────────────────────

  const handleBookmark = async () => {
    if (!currentUser) { onAuthRequired?.('bookmark'); return; }
    if (bookmarkLoading) return;

    const wasBookmarked = isBookmarked;
    // Optimistic update
    setIsBookmarked(!wasBookmarked);
    setBookmarkCount(c => wasBookmarked ? Math.max(0, c - 1) : c + 1);
    setBookmarkLoading(true);
    clearTimeout(notePromptTimer.current);
    setNotePrompt(null);

    try {
      if (wasBookmarked) {
        const { bookmarkCount: newCount } = await Storage.removeBookmark(tl.id);
        setBookmarkCount(newCount);
      } else {
        const { bookmarkCount: newCount } = await Storage.addBookmark(tl.id);
        setBookmarkCount(newCount);
        // Show note prompt for 5 seconds
        setNotePrompt('showing');
        setBookmarkNote('');
        notePromptTimer.current = setTimeout(() => setNotePrompt('dismissed'), 5000);
      }
    } catch (err) {
      // Revert
      setIsBookmarked(wasBookmarked);
      setBookmarkCount(c => wasBookmarked ? c + 1 : Math.max(0, c - 1));
      showToast(err?.message || 'Could not update bookmark', 'error');
    } finally {
      setBookmarkLoading(false);
    }
  };

  const handleSaveBookmarkNote = async () => {
    clearTimeout(notePromptTimer.current);
    setNotePrompt('dismissed');
    if (!bookmarkNote.trim()) return;
    try {
      await Storage.updateBookmarkNote(tl.id, bookmarkNote.trim());
    } catch { /* note is non-critical */ }
  };

  // ── Comment likes ────────────────────────────────────────────────────────────

  const handleCommentLike = async (id, isReply = false) => {
    if (!currentUser) { onAuthRequired?.('like'); return; }
    const cur = commentLikes[id] || { count: 0, liked: false };
    setCommentLikes(prev => ({ ...prev, [id]: { count: cur.liked ? cur.count - 1 : cur.count + 1, liked: !cur.liked } }));
    try {
      const fn = isReply
        ? (cur.liked ? Storage.unlikeReply : Storage.likeReply)
        : (cur.liked ? Storage.unlikeComment : Storage.likeComment);
      const newCount = await fn(id);
      setCommentLikes(prev => ({ ...prev, [id]: { count: newCount, liked: !cur.liked } }));
    } catch (err) {
      setCommentLikes(prev => ({ ...prev, [id]: cur }));
      showToast(err?.message || 'Could not update like', 'error');
    }
  };

  const handleLikerHover = async (id) => {
    if (likerTips[id] !== undefined) return;
    setLikerTips(prev => ({ ...prev, [id]: null }));
    try {
      const likers = await Storage.getCommentLikers(id);
      setLikerTips(prev => ({ ...prev, [id]: likers }));
    } catch { setLikerTips(prev => ({ ...prev, [id]: [] })); }
  };

  // ── @mention autocomplete ────────────────────────────────────────────────────

  const handleMentionInput = useCallback((text, fieldType) => {
    const at = text.lastIndexOf('@');
    if (at === -1) { setMentionActive(null); return; }
    const afterAt = text.slice(at + 1);
    if (/\s/.test(afterAt) && afterAt.length > 0) { setMentionActive(null); return; }
    if (!afterAt) { setMentionActive(null); return; }
    setMentionQuery(afterAt);
    setMentionActive(fieldType);
    setMentionIndex(0);
    clearTimeout(mentionDebounce.current);
    mentionDebounce.current = setTimeout(async () => {
      try {
        const users = await Storage.searchUsers(afterAt);
        setMentionResults(users);
      } catch { setMentionResults([]); }
    }, 200);
  }, []);

  const insertMention = useCallback((user, fieldType) => {
    const isComment = fieldType === 'comment';
    const text = isComment ? commentText : replyText;
    const setText = isComment ? setCommentText : setReplyText;
    const at = text.lastIndexOf('@');
    setText(text.slice(0, at) + `@${user.name} `);
    setMentionActive(null);
    setMentionResults([]);
    if (isComment) commentTextareaRef.current?.focus();
    else           replyTextareaRef.current?.focus();
  }, [commentText, replyText]);

  const handleCommentKeyDown = (e) => {
    if (mentionActive === 'comment' && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionResults.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionResults[mentionIndex], 'comment'); return; }
      if (e.key === 'Escape') { setMentionActive(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); }
  };

  const handleReplyKeyDown = (e) => {
    if (mentionActive === 'reply' && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionResults.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionResults[mentionIndex], 'reply'); return; }
      if (e.key === 'Escape') { setMentionActive(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); }
  };

  // ── Blocking ─────────────────────────────────────────────────────────────────

  const executeBlock = async () => {
    if (!blockConfirm) return;
    try {
      await Storage.blockUser(blockConfirm.userId);
      setLocalBlockedIds(prev => new Set([...prev, blockConfirm.userId]));
      onBlock?.(blockConfirm.userId);
      showToast(`${blockConfirm.userName} blocked.`, 'success');
    } catch { showToast('Could not block user.', 'error'); }
    finally { setBlockConfirm(null); }
  };

  // Render like button for a comment or reply
  const renderLikeButton = (id, isReply = false) => {
    const state = commentLikes[id] || { count: 0, liked: false };
    const isAuth = !!currentUser;
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          onClick={() => isAuth ? handleCommentLike(id, isReply) : onAuthRequired?.('like')}
          aria-label={state.liked ? 'Unlike' : 'Like'}
          style={{
            background: 'none', border: 'none', cursor: isAuth ? 'pointer' : 'default',
            display: 'inline-flex', alignItems: 'center', gap: 3,
            color: state.liked ? '#e05c5c' : 'var(--text3)', opacity: isAuth ? 1 : 0.6,
            fontSize: 12, padding: '2px 4px', borderRadius: 4, transition: 'color 0.15s',
          }}
          onMouseEnter={e => {
            if (isAuth && !state.liked) e.currentTarget.style.color = '#e05c5c';
            if (state.count > 0) handleLikerHover(id);
          }}
          onMouseLeave={e => { if (!state.liked) e.currentTarget.style.color = 'var(--text3)'; }}
        >
          <Icon name="heart" size={12} fill={state.liked ? 'currentColor' : 'none'} color="currentColor" />
          {state.count > 0 && <span>{state.count}</span>}
        </button>
        {state.count > 0 && likerTips[id] && likerTips[id].length > 0 && (
          <div className="liker-tooltip" style={{
            position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, zIndex: 60,
            background: 'var(--surface)', border: '1px solid var(--border2)',
            borderRadius: 'var(--radius)', padding: '6px 10px',
            minWidth: 120, maxWidth: 200, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            pointerEvents: 'none', fontSize: 12,
          }}>
            {(() => {
              const shown = likerTips[id].slice(0, 5);
              const rest  = likerTips[id].length - shown.length;
              const names = shown.map(l => l.userName).join(', ');
              return <span style={{ color: 'var(--text2)' }}>
                Liked by {names}{rest > 0 ? ` and ${rest} other${rest !== 1 ? 's' : ''}` : ''}
              </span>;
            })()}
          </div>
        )}
      </div>
    );
  };

  // ── Navigation with search context ───────────────────────────────────────────
  // Attaches _searchQuery and _matchedEventIds to the tl object so TimelineViewer
  // can highlight matching events when opened from search results.
  const handleViewTimeline = () => {
    const enriched = (matchedEvents.length > 0 || searchQuery)
      ? { ...tl, _searchQuery: searchQuery, _matchedEventIds: matchedEvents.map(e => e.id) }
      : tl;
    onViewTimeline(enriched, author);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <article
      className="feed-post"
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)', overflow: 'hidden', transition: 'border-color 0.2s',
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = colors.secondary}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
    >
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, background: 'var(--surface)', border: '1px solid var(--border2)',
          borderRadius: 'var(--radius)', padding: '10px 20px', fontSize: 13,
          color: toast.type === 'error' ? 'var(--danger)' : toast.type === 'success' ? 'var(--success)' : 'var(--text)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)', pointerEvents: 'none',
        }}>
          {toast.message}
        </div>
      )}

      {/* Background image / color strip */}
      {getTimelinePreviewImage(tl) ? (
        <div style={{ height: 160, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
          <img src={getTimelinePreviewImage(tl)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 72, background: 'linear-gradient(to bottom, transparent, var(--surface))' }} aria-hidden="true" />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})` }} aria-hidden="true" />
          {/* Trending rank badge overlaid on image */}
          {isTrendingTab && trendingRank != null && (
            <div className={`trending-rank-badge rank-${Math.min(trendingRank, 4)}`} aria-label={`Trending rank ${trendingRank}`}>
              #{trendingRank}
            </div>
          )}
        </div>
      ) : (
        <div style={{ height: 8, background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`, position: 'relative' }} aria-hidden="true">
          {/* Trending rank badge on color strip */}
          {isTrendingTab && trendingRank != null && (
            <div className={`trending-rank-badge rank-${Math.min(trendingRank, 4)}`} aria-label={`Trending rank ${trendingRank}`}>
              #{trendingRank}
            </div>
          )}
        </div>
      )}

      <div className="feed-post-body" style={{ padding: '16px 20px 20px' }}>

        {/* ── Author row ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div
            style={{ position: 'relative', flexShrink: 0, cursor: 'pointer' }}
            onMouseEnter={() => setShowAuthorTip(true)}
            onMouseLeave={() => setShowAuthorTip(false)}
            onClick={() => onViewProfile?.(author)}
            role="button" tabIndex={0} aria-label={`View ${author.name}'s profile`}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onViewProfile?.(author); }}
          >
            {renderAvatar(author, 36)}
            {showAuthorTip && (
              <div style={{
                position: 'absolute', left: 0, top: 44, zIndex: 50,
                background: 'var(--surface)', border: '1px solid var(--border2)',
                borderRadius: 'var(--radius-lg)', padding: '14px 16px',
                minWidth: 220, maxWidth: 280, boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                pointerEvents: 'none',
              }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: author.bio ? 10 : 0 }}>
                  {renderAvatar(author, 40)}
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 2 }}>{author.name}</p>
                    {author.createdAt && (
                      <p style={{ fontSize: 11, color: 'var(--text3)' }}>
                        Member since {new Date(author.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                      </p>
                    )}
                  </div>
                </div>
                {author.bio && <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, margin: 0 }}>{author.bio}</p>}
                <p style={{ fontSize: 11, color: 'var(--accent)', marginTop: 8, marginBottom: 0 }}>Click to view profile</p>
              </div>
            )}
          </div>

          <div
            style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
            onClick={() => onViewProfile?.(author)} role="button" tabIndex={0}
            aria-label={`View ${author.name}'s profile`}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onViewProfile?.(author); }}
          >
            <p style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text)'}
            >{author.name}</p>
            <p style={{ fontSize: 11, color: 'var(--text3)' }}>{timeAgo(tl.updatedAt || tl.createdAt)}</p>
          </div>

          {onFollowToggle && author.id !== currentUser?.id && (
            <button
              onClick={e => { e.stopPropagation(); onFollowToggle(author.id); }}
              aria-label={isFollowing ? `Unfollow ${author.name}` : `Follow ${author.name}`}
              style={{
                flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s',
                border: isFollowing ? '1px solid var(--border2)' : '1px solid var(--accent)',
                background: isFollowing ? 'transparent' : 'var(--accent)',
                color: isFollowing ? 'var(--text2)' : 'var(--on-accent)',
              }}
              onMouseEnter={e => { if (isFollowing) e.currentTarget.style.borderColor = 'var(--danger)'; }}
              onMouseLeave={e => { if (isFollowing) e.currentTarget.style.borderColor = 'var(--border2)'; }}
            >
              {isFollowing ? '✓ Following' : '+ Follow'}
            </button>
          )}
        </div>

        {/* ── Title & orientation ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: tl.category ? 8 : 10 }}>
          <h3 onClick={() => handleViewTimeline()}
            style={{ fontFamily: 'var(--font-display)', fontSize: 17, flex: 1, marginRight: 8, cursor: 'pointer', transition: 'color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
            onMouseLeave={e => e.currentTarget.style.color = ''}
          >{tl.title}</h3>
          <span style={{ fontSize: 10, background: 'var(--bg3)', borderRadius: 6, padding: '3px 8px', color: 'var(--text3)', flexShrink: 0 }}>{tl.orientation}</span>
        </div>

        {tl.category && (() => {
          const cat = TIMELINE_CATEGORIES.find(c => c.value === tl.category);
          return cat ? (
            <div style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 11, background: 'var(--bg3)', borderRadius: 6, padding: '3px 9px', color: 'var(--text2)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span aria-hidden="true">{cat.emoji}</span> {cat.label}
              </span>
            </div>
          ) : null;
        })()}

        {hasTextContent(tl.description) && (
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 10, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {stripHtml(tl.description)}
          </p>
        )}

        {/* Trending engagement badge — only visible on Trending tab */}
        {isTrendingTab && (() => {
          const max = Math.max(likes7d * 3, comments7d * 5, views7d);
          if (max === 0) return null;
          let badge;
          if (comments7d * 5 >= likes7d * 3 && comments7d > 0) {
            badge = `💬 ${comments7d} comment${comments7d !== 1 ? 's' : ''} this week`;
          } else if (likes7d > 0) {
            badge = `🔥 ${likes7d} like${likes7d !== 1 ? 's' : ''} this week`;
          } else {
            badge = `👁 ${views7d} view${views7d !== 1 ? 's' : ''} this week`;
          }
          return <div className="trending-engagement-badge">{badge}</div>;
        })()}

        {/* Matched events badge — shown when keyword search found events inside this timeline */}
        {matchedEvents.length > 0 && (
          <button
            type="button"
            onClick={() => handleViewTimeline()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              color: 'var(--accent)', background: 'none', border: 'none',
              padding: '3px 0', marginBottom: 4, textDecoration: 'underline',
              textDecorationStyle: 'dotted', textUnderlineOffset: 3,
            }}
            aria-label={`View ${matchedEvents.length} matching event${matchedEvents.length !== 1 ? 's' : ''}`}
          >
            <Icon name="search" size={11} />
            {matchedEvents.length} matching event{matchedEvents.length !== 1 ? 's' : ''}
          </button>
        )}

        <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: (authorNote || isAuthor) ? 12 : 0 }}>
          {tl.events.length === 0 ? 'No events yet' : `${tl.events.length} event${tl.events.length !== 1 ? 's' : ''}`}
          {tl.orientation === 'storyline' && Array.isArray(tl.chapters) && tl.chapters.length > 0 && (
            <> · {tl.chapters.length} chapter{tl.chapters.length !== 1 ? 's' : ''}</>
          )}
          {minDate && ` · ${formatDate(minDate, minShowEra)} → ${formatDate(maxDate, maxShowEra)}`}
        </p>

        {/* Author note */}
        {!editingNote && authorNote && (
          <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 0, borderLeft: `3px solid ${colors.primary}80` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, margin: 0, flex: 1 }}>{authorNote}</p>
              {isAuthor && (
                <button onClick={() => { setNoteText(authorNote); setEditingNote(true); }} aria-label="Edit note"
                  style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: '2px 4px', flexShrink: 0 }}>
                  <Icon name="pencil" size={12} />
                </button>
              )}
            </div>
          </div>
        )}
        {!editingNote && !authorNote && isAuthor && (
          <button onClick={() => { setNoteText(''); setEditingNote(true); }}
            style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 12, padding: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Icon name="pencil" size={12} /> Add a note about this post…
          </button>
        )}
        {editingNote && (
          <div>
            <textarea value={noteText} onChange={e => setNoteText(e.target.value.slice(0, 300))}
              placeholder="Add a note about this post…" rows={2} autoFocus
              style={{ resize: 'vertical', width: '100%', marginBottom: 4, fontSize: 13, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: noteText.length >= 280 ? 'var(--danger)' : 'var(--text3)' }}>{noteText.length}/300</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost btn-sm" onClick={() => setEditingNote(false)}>Cancel</button>
                <button className="btn-primary btn-sm" onClick={handleSaveNote}>Save</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Action bar ── */}
        <div className="feed-action-bar" style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <div className="feed-action-bar-top">
          <button onClick={() => handleViewTimeline()} className="btn-ghost btn-sm feed-view-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="eye" size={14} /> View
          </button>
        </div>
        <div className="feed-action-bar-actions">

          {/* Reaction button + picker */}
          <div
            style={{ position: 'relative' }}
            onMouseEnter={() => { clearTimeout(reactionPickerTimer.current); setShowReactionPicker(true); }}
            onMouseLeave={() => { reactionPickerTimer.current = setTimeout(() => setShowReactionPicker(false), 200); }}
          >
            {/* Reaction picker flyout */}
            {showReactionPicker && currentUser && (
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, zIndex: 60,
                display: 'flex', gap: 4, padding: '6px 8px',
                background: 'var(--surface)', border: '1px solid var(--border2)',
                borderRadius: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
              }}>
                {REACTIONS.map(r => (
                  <button
                    key={r.type}
                    type="button"
                    onClick={() => handleReact(r.type)}
                    aria-label={r.label}
                    title={r.label}
                    style={{
                      background: (userReaction?.reactionType || 'heart') === r.type && isReacted
                        ? 'var(--bg3)' : 'none',
                      border: 'none', borderRadius: 8, padding: '4px 6px',
                      fontSize: 18, cursor: 'pointer',
                      transform: (userReaction?.reactionType || 'heart') === r.type && isReacted ? 'scale(1.25)' : 'scale(1)',
                      transition: 'transform 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.35)'}
                    onMouseLeave={e => {
                      const active = (userReaction?.reactionType || 'heart') === r.type && isReacted;
                      e.currentTarget.style.transform = active ? 'scale(1.25)' : 'scale(1)';
                    }}
                  >
                    {r.emoji}
                  </button>
                ))}
              </div>
            )}

            {/* Main reaction button */}
            <button
              onClick={() => handleReact(userReaction?.reactionType || 'heart')}
              aria-label={isReacted ? `Remove ${userReaction.reactionType} reaction` : 'React'}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: isReacted ? 'rgba(224,92,92,0.12)' : 'transparent',
                border: `1px solid ${isReacted ? 'rgba(224,92,92,0.4)' : 'transparent'}`,
                borderRadius: 8, padding: '6px 10px',
                color: isReacted ? '#e05c5c' : 'var(--text2)',
                cursor: 'pointer', fontSize: 13, transition: 'all 0.15s',
              }}
            >
              {isReacted
                ? <span style={{ fontSize: 15, lineHeight: 1 }}>{REACTIONS.find(r => r.type === (userReaction.reactionType || 'heart'))?.emoji ?? '❤️'}</span>
                : <Icon name="heart" size={15} fill="none" color="currentColor" />
              }
              {/* Per-reaction-type counts */}
              {(() => {
                const counts = likes.reduce((acc, l) => {
                  const t = l.reactionType || 'heart';
                  acc[t] = (acc[t] || 0) + 1;
                  return acc;
                }, {});
                const active = REACTIONS.filter(r => counts[r.type]);
                if (!active.length) return null;
                return (
                  <span style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    {active.map(r => (
                      <span key={r.type} style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 12 }}>
                        <span style={{ fontSize: 13 }}>{r.emoji}</span>
                        <span>{counts[r.type]}</span>
                      </span>
                    ))}
                  </span>
                );
              })()}
            </button>
          </div>

          {/* Comment button */}
          <button
            onClick={() => setShowComments(v => !v)}
            aria-label={`${totalComments} comment${totalComments !== 1 ? 's' : ''}`}
            aria-expanded={showComments}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: showComments ? 'var(--accent)18' : 'transparent',
              border: `1px solid ${showComments ? 'var(--accent)50' : 'transparent'}`,
              borderRadius: 8, padding: '6px 10px',
              color: showComments ? 'var(--accent)' : 'var(--text2)',
              cursor: 'pointer', fontSize: 13, transition: 'all 0.15s',
            }}
          >
            <Icon name="message" size={15} />
            {totalComments > 0 && <span>{totalComments}</span>}
          </button>

          {/* Share button */}
          <button
            onClick={handleShare} aria-label="Copy link"
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: shareCopied ? 'rgba(91,184,91,0.12)' : 'transparent',
              border: `1px solid ${shareCopied ? 'rgba(91,184,91,0.4)' : 'transparent'}`,
              borderRadius: 8, padding: '6px 10px',
              color: shareCopied ? '#5cb85c' : 'var(--text2)',
              cursor: 'pointer', fontSize: 13, transition: 'all 0.15s',
            }}
          >
            <Icon name="link" size={15} />
            <span className="feed-share-text">{shareCopied ? 'Copied!' : 'Share'}</span>
          </button>

          {/* Bookmark button */}
          <button
            onClick={handleBookmark}
            aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark this timeline'}
            disabled={bookmarkLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: isBookmarked ? 'color-mix(in srgb, var(--accent) 12%, transparent)' : 'transparent',
              border: `1px solid ${isBookmarked ? 'color-mix(in srgb, var(--accent) 35%, transparent)' : 'transparent'}`,
              borderRadius: 8, padding: '6px 10px',
              color: isBookmarked ? 'var(--accent)' : 'var(--text2)',
              cursor: bookmarkLoading ? 'default' : 'pointer', fontSize: 13, transition: 'all 0.15s',
              opacity: bookmarkLoading ? 0.6 : 1,
            }}
            onMouseEnter={e => { if (!isBookmarked && !bookmarkLoading) e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={e => { if (!isBookmarked) e.currentTarget.style.color = 'var(--text2)'; }}
          >
            <Icon name="bookmark" size={15} fill={isBookmarked ? 'currentColor' : 'none'} color="currentColor" />
            {bookmarkCount > 0 && <span>{bookmarkCount}</span>}
          </button>

          {/* View count — read-only stat, blends with other engagement counts. */}
          {(tl.viewCount ?? 0) > 0 && (
            <span
              aria-label={`${tl.viewCount} view${tl.viewCount === 1 ? '' : 's'}`}
              title={`${tl.viewCount.toLocaleString()} views`}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 10px',
                color: 'var(--text2)', fontSize: 13,
              }}
            >
              <Icon name="eye" size={15} />
              <span>{formatCompactCount(tl.viewCount)}</span>
            </span>
          )}

          {/* Add to collection button */}
          {currentUser && (
            <button
              onClick={() => setShowCollectionModal(true)}
              aria-label="Add to collection"
              title="Add to collection"
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'transparent', border: '1px solid transparent',
                borderRadius: 8, padding: '6px 10px',
                color: 'var(--text2)', cursor: 'pointer', fontSize: 13, transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text2)'}
            >
              <Icon name="layers" size={15} />
            </button>
          )}

          {/* Report button */}
          {currentUser && !isAuthor && (
            <button
              onClick={() => !hasReported && setShowReport(true)}
              aria-label={hasReported ? 'Already reported' : 'Report this timeline'}
              title={hasReported ? 'Reported' : 'Report'}
              disabled={hasReported}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'transparent', border: '1px solid transparent',
                borderRadius: 8, padding: '6px 10px',
                color: 'var(--text3)', cursor: hasReported ? 'default' : 'pointer', fontSize: 13,
                opacity: hasReported ? 0.5 : 1, transition: 'all 0.15s',
              }}
              onMouseEnter={e => { if (!hasReported) e.currentTarget.style.color = 'var(--danger)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; }}
            >
              <Icon name="flag" size={14} />
              <span style={{ fontSize: 12 }}>{hasReported ? 'Reported' : ''}</span>
            </button>
          )}

          {/* Block author from feed */}
          {currentUser && !isAuthor && (
            <button
              onClick={() => setBlockConfirm({ userId: author.id, userName: author.name })}
              aria-label={`Block ${author.name}`} title="Block user"
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'transparent', border: '1px solid transparent',
                borderRadius: 8, padding: '6px 10px',
                color: 'var(--text3)', cursor: 'pointer', fontSize: 13, transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
            >
              <Icon name="ban" size={14} />
            </button>
          )}

        </div>
        </div>

        {/* Bookmark note prompt — appears after bookmarking, auto-dismisses in 5 s */}
        {notePrompt === 'showing' && (
          <div className="bookmark-note-prompt">
            <span style={{ fontSize: 12, color: 'var(--text2)' }}>Add a personal note? <span style={{ color: 'var(--text3)' }}>(optional)</span></span>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input
                type="text"
                value={bookmarkNote}
                onChange={e => setBookmarkNote(e.target.value.slice(0, 200))}
                placeholder="Your note…"
                style={{ flex: 1, fontSize: 12, padding: '5px 10px' }}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveBookmarkNote(); if (e.key === 'Escape') { clearTimeout(notePromptTimer.current); setNotePrompt('dismissed'); } }}
                autoFocus
              />
              <button
                onClick={handleSaveBookmarkNote}
                className="btn-primary btn-sm"
                style={{ fontSize: 11, padding: '5px 10px' }}
              >Save</button>
              <button
                onClick={() => { clearTimeout(notePromptTimer.current); setNotePrompt('dismissed'); }}
                className="btn-ghost btn-sm"
                style={{ fontSize: 11, padding: '5px 10px' }}
              >Skip</button>
            </div>
          </div>
        )}

        <AddToCollectionModal
          open={showCollectionModal}
          onClose={() => setShowCollectionModal(false)}
          timelineId={tl.id}
          timelineName={tl.title}
          currentUser={currentUser}
        />
        <ReportModal open={showReport} onClose={() => setShowReport(false)} contentType="timeline" contentId={tl.id} onReported={() => setHasReported(true)} />
        <ReportModal
          open={!!commentReportTarget}
          onClose={() => setCommentReportTarget(null)}
          contentType={commentReportTarget?.type}
          contentId={commentReportTarget?.id}
          onReported={() => { setReportedCommentIds(p => new Set([...p, commentReportTarget?.id])); setCommentReportTarget(null); }}
        />

        {/* ── Block confirmation ── */}
        <Modal open={!!blockConfirm} onClose={() => setBlockConfirm(null)} title="Block User" width={400}>
          {blockConfirm && (
            <div>
              <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                Block <strong style={{ color: 'var(--text)' }}>{blockConfirm.userName}</strong>?
                They will not be able to comment on your timelines or follow you.
                <br /><span style={{ color: 'var(--text3)', fontSize: 12 }}>They will not be notified that you blocked them.</span>
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn-ghost btn-sm" onClick={() => setBlockConfirm(null)}>Cancel</button>
                <button className="btn-danger" onClick={executeBlock}>Block</button>
              </div>
            </div>
          )}
        </Modal>

        {/* ── Comments section ── */}
        {showComments && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>

            {/* New comment input */}
            {!currentUser ? (
              <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: 'var(--surface2)', textAlign: 'center', fontSize: 13, color: 'var(--text2)' }}>
                <button className="btn-link" style={{ fontWeight: 600 }} onClick={() => onAuthRequired?.('comment')}>Sign in</button> to leave a comment
              </div>
            ) : (
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-start' }}>
              {renderAvatar(currentUser, 30)}
              <div style={{ flex: 1, position: 'relative' }}>
                <textarea
                  ref={commentTextareaRef}
                  value={commentText}
                  onChange={e => { setCommentText(e.target.value.slice(0, 500)); handleMentionInput(e.target.value, 'comment'); }}
                  placeholder="Write a comment… (use @ to mention someone)"
                  rows={2}
                  style={{ resize: 'none', width: '100%', marginBottom: 6, fontSize: 13, boxSizing: 'border-box' }}
                  onKeyDown={handleCommentKeyDown}
                  onBlur={() => setTimeout(() => setMentionActive(null), 150)}
                />
                {mentionActive === 'comment' && mentionResults.length > 0 && (
                  <div className="mention-dropdown">
                    {mentionResults.filter(Boolean).map((u, i) => (
                      <button key={u.id} className={`mention-option${i === mentionIndex ? ' selected' : ''}`}
                        onMouseDown={e => { e.preventDefault(); insertMention(u, 'comment'); }}>
                        {u.avatar
                          ? <img src={u.avatar} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
                          : <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent)33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'var(--accent)' }}>{(u.name || '?')[0]}</div>
                        }
                        <span>{u.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
                  {commentText.length > 440 && (
                    <span style={{ fontSize: 11, color: commentText.length >= 500 ? 'var(--danger)' : 'var(--text3)' }}>
                      {commentText.length}/500
                    </span>
                  )}
                  <button className="btn-primary btn-sm" onClick={handleComment} disabled={!commentText.trim()} style={{ opacity: commentText.trim() ? 1 : 0.5 }}>
                    Comment
                  </button>
                </div>
              </div>
            </div>
            )}

            {comments.filter(c => !allBlockedIds.has(c.userId)).length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: '8px 0' }}>No comments yet. Be the first!</p>
            )}

            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {comments.filter(c => !allBlockedIds.has(c.userId)).map(c => (
                <li key={c.id} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {renderAvatar({ name: c.userName, avatar: c.userAvatar }, 30)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{c.userName}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{timeAgo(c.timestamp)}</span>
                            {currentUser && c.userId !== currentUser?.id && !reportedCommentIds.has(c.id) && (
                              <button onClick={() => setCommentReportTarget({ type: 'comment', id: c.id })} aria-label="Report comment" title="Report"
                                style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                              ><Icon name="flag" size={12} /></button>
                            )}
                            {currentUser && c.userId !== currentUser?.id && !allBlockedIds.has(c.userId) && (
                              <button onClick={() => setBlockConfirm({ userId: c.userId, userName: c.userName })} aria-label="Block user" title="Block user"
                                style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                              ><Icon name="ban" size={12} /></button>
                            )}
                            {c.userId === currentUser?.id && (
                              <button onClick={() => handleDeleteComment(c.id)} aria-label="Delete comment"
                                style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                              ><Icon name="trash" size={12} /></button>
                            )}
                          </div>
                        </div>
                        <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, margin: 0 }}>{renderMentionText(c.text)}</p>
                      </div>

                      {/* Like + reply footer */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {renderLikeButton(c.id, false)}
                        <button
                          onClick={() => { if (!currentUser) { onAuthRequired?.('comment'); return; } setReplyingTo(replyingTo === c.id ? null : c.id); setReplyText(''); }}
                          style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 12, padding: '2px 4px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <Icon name="reply" size={12} />
                          {c.replies?.length > 0 ? `${c.replies.length} repl${c.replies.length !== 1 ? 'ies' : 'y'}` : 'Reply'}
                        </button>
                      </div>

                      {replyingTo === c.id && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-start' }}>
                          {renderAvatar(currentUser, 24)}
                          <div style={{ flex: 1, position: 'relative' }}>
                            <textarea
                              ref={replyTextareaRef}
                              value={replyText}
                              onChange={e => { setReplyText(e.target.value.slice(0, 500)); handleMentionInput(e.target.value, 'reply'); }}
                              placeholder={`Reply to ${c.userName}… (use @ to mention)`}
                              rows={2} autoFocus
                              style={{ resize: 'none', width: '100%', marginBottom: 6, fontSize: 12, boxSizing: 'border-box' }}
                              onKeyDown={handleReplyKeyDown}
                              onBlur={() => setTimeout(() => setMentionActive(null), 150)}
                            />
                            {mentionActive === 'reply' && mentionResults.length > 0 && (
                              <div className="mention-dropdown">
                                {mentionResults.filter(Boolean).map((u, i) => (
                                  <button key={u.id} className={`mention-option${i === mentionIndex ? ' selected' : ''}`}
                                    onMouseDown={e => { e.preventDefault(); insertMention(u, 'reply'); }}>
                                    {u.avatar
                                      ? <img src={u.avatar} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />
                                      : <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--accent)33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'var(--accent)' }}>{(u.name || '?')[0]}</div>
                                    }
                                    <span>{u.name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                              <button className="btn-ghost btn-sm" onClick={() => { setReplyingTo(null); setReplyText(''); }}>Cancel</button>
                              <button className="btn-primary btn-sm" onClick={() => handleReply(c.id)} disabled={!replyText.trim()} style={{ opacity: replyText.trim() ? 1 : 0.5 }}>
                                Reply
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {c.replies?.filter(r => !allBlockedIds.has(r.userId)).length > 0 && (
                        <ol style={{ listStyle: 'none', padding: '8px 0 0 0', margin: 0, borderLeft: '2px solid var(--border)', paddingLeft: 12 }}>
                          {c.replies.filter(r => !allBlockedIds.has(r.userId)).map(r => (
                            <li key={r.id} style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
                              {renderAvatar({ name: r.userName, avatar: r.userAvatar }, 24)}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '8px 10px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                                    <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--text)' }}>{r.userName}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>{timeAgo(r.timestamp)}</span>
                                      {currentUser && r.userId !== currentUser?.id && !reportedCommentIds.has(r.id) && (
                                        <button onClick={() => setCommentReportTarget({ type: 'reply', id: r.id })} aria-label="Report reply" title="Report"
                                          style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                                        ><Icon name="flag" size={11} /></button>
                                      )}
                                      {currentUser && r.userId !== currentUser?.id && !allBlockedIds.has(r.userId) && (
                                        <button onClick={() => setBlockConfirm({ userId: r.userId, userName: r.userName })} aria-label="Block user" title="Block user"
                                          style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                                        ><Icon name="ban" size={11} /></button>
                                      )}
                                      {r.userId === currentUser?.id && (
                                        <button onClick={() => handleDeleteReply(c.id, r.id)} aria-label="Delete reply"
                                          style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, display: 'flex', transition: 'color 0.15s' }}
                                          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
                                        ><Icon name="trash" size={11} /></button>
                                      )}
                                    </div>
                                  </div>
                                  <p style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, margin: 0 }}>{renderMentionText(r.text)}</p>
                                </div>
                                <div style={{ marginTop: 2 }}>
                                  {renderLikeButton(r.id, true)}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </article>
  );
};

export default FeedPost;
